<template>
  <div class="view system staff">
    <a-layout v-if="staff">
      <a-layout-header>
        <a-page-header
          title="Staff Details"
          :sub-title="`${staff.display_name ? staff.display_name : ''} (${staff.username})`"
          @back="() => $router.push('/system/staffs')"
        />
      </a-layout-header>
      <div class="information">
        <a-descriptions size="small" :column="2" bordered>
          <a-descriptions-item label="Staff ID" span="2"> {{ staff._id }} </a-descriptions-item>
          <a-descriptions-item label="Creator" v-if="staff.created_by">
            {{ staff.created_by.display_name }}
          </a-descriptions-item>
          <a-descriptions-item label="Updator" v-if="staff.updated_by">
            {{ staff.updated_by.display_name }}
          </a-descriptions-item>
          <a-descriptions-item label="Created At" v-if="staff.created_at">
            <span class="badge badge-primary"> {{ staff.created_at | moment('lll') }}</span>
          </a-descriptions-item>
          <a-descriptions-item label="Updated At" v-if="staff.updated_at">
            <span class="badge badge-success"> {{ staff.updated_at | moment('lll') }}</span>
          </a-descriptions-item>
        </a-descriptions>
      </div>
      <a-layout-content class="rounded d-flex flex-column flex-md-row">
        <div class="sider border-right mt-3 p-3 d-flex flex-column align-items-center" style="gap: 0.5rem">
          <a-avatar :size="180" :src="staff.avatar" shape="square" icon="user" />
          <span>{{ staff.name + ' ' + staff.lastname }}</span>
          <span>{{ staff.username }}</span>
          <span>{{ staff.email }}</span>
          <a-tag color="#108ee9"> <i class="fe fe-clock"></i> {{ staff.created_at | moment('lll') }} </a-tag>
          <a-tag color="#87d068"> <i class="fe fe-refresh-cw"></i> {{ staff.updated_at | moment('lll') }} </a-tag>
        </div>
        <a-form-model class="p-3 flex-grow-1" :model="staff" :label-col="labelCol" :wrapper-col="wrapperCol">
          <!-- Authentication -->
          <a-form-model-item label="Authentication">
            <a-input-group compact>
              <a-input v-model="staff.username" style="width: 50%" placeholder="Username">
                <a-icon slot="prefix" type="user" style="color: rgba(0, 0, 0, 0.25)" />
              </a-input>
              <a-input v-model="staff.password" style="width: 50%" placeholder="Password" type="password">
                <a-icon slot="prefix" type="lock" style="color: rgba(0, 0, 0, 0.25)" />
              </a-input>
            </a-input-group>
          </a-form-model-item>

          <!-- Name -->
          <a-form-model-item label="Name/Lastname">
            <a-input-group compact>
              <a-input v-model="staff.name" style="width: 50%" placeholder="Name" />
              <a-input v-model="staff.lastname" style="width: 50%" placeholder="Last Name" />
            </a-input-group>
          </a-form-model-item>

          <!-- Display Name -->
          <a-form-model-item label="Display Name">
            <a-input v-model="staff.display_name" placeholder="Display Name" />
          </a-form-model-item>

          <!-- Email -->
          <a-form-model-item label="Email">
            <a-input v-model="staff.email" placeholder="Email" />
          </a-form-model-item>

          <!-- Phone -->
          <a-form-model-item label="Phone">
            <a-input-group compact>
              <a-input v-model="staff.phone" style="width: 50%" placeholder="Number" />
              <a-input v-model="staff.extension" style="width: 50%" placeholder="Extension" />
            </a-input-group>
          </a-form-model-item>

          <!-- Department/Job Title -->
          <a-form-model-item label="Department/Job Title">
            <a-input-group compact>
              <a-select v-model="staff.department" default-value="tech" style="width: 50%">
                <a-select-option
                  v-for="department in options_['DEPARTMENT']"
                  :key="department.key"
                  :value="department.key"
                >
                  {{ department.value }}
                </a-select-option>
              </a-select>

              <a-select v-model="staff.job_title" default-value="tech" style="width: 50%">
                <a-select-option
                  v-for="job_title in options_['JOB_TITLES']"
                  :key="job_title.key"
                  :value="job_title.key"
                >
                  {{ job_title.value }}
                </a-select-option>
              </a-select>
            </a-input-group>
          </a-form-model-item>

          <!-- Location -->
          <a-form-model-item label="Location">
            <a-input v-model="staff.location" />
          </a-form-model-item>

          <!-- Address -->
          <a-form-model-item label="Address">
            <a-input v-model="staff.address" />
          </a-form-model-item>

          <!-- Bio -->
          <a-form-model-item label="Bio">
            <a-input v-model="staff.bio" type="textarea" :rows="4" />
          </a-form-model-item>

          <!-- Note -->
          <a-form-model-item label="Note">
            <a-input v-model="staff.note" type="textarea" />
          </a-form-model-item>

          <!-- Roles/Permissions -->
          <a-form-model-item label="Roles/Permissions">
            <a-input-group compact>
              <a-select v-model="staff.roles" default-value="employee" style="width: 50%">
                <a-select-option value="superadmin"> Superadmin </a-select-option>
                <a-select-option value="admin"> Admin </a-select-option>
                <a-select-option value="manager"> Manager </a-select-option>
                <a-select-option value="employee"> Employee </a-select-option>
                <a-select-option value="Worker"> Worker </a-select-option>
                <a-select-option value="visitor"> Visitor </a-select-option>
              </a-select>

              <a-select mode="tags" v-model="staff.permissions" default-value="tech" style="width: 50%">
                <a-select-option value="all"> All </a-select-option>
                <a-select-option value="default"> Default </a-select-option>
                <a-select-option value="crm"> CRM </a-select-option>
                <a-select-option value="pm"> PM </a-select-option>
                <a-select-option value="marketing"> MARKETING </a-select-option>
                <a-select-option value="system"> SYSTEM </a-select-option>
              </a-select>
            </a-input-group>
          </a-form-model-item>

          <!-- Active -->
          <a-form-model-item label="Active" prop="active">
            <a-switch v-model="staff.active" />
          </a-form-model-item>

          <!-- Actions -->
          <a-form-model-item :wrapper-col="{ span: 14, offset: 4 }">
            <a-button type="primary" @click="update"> Update </a-button>
            <a-button style="margin-left: 10px" @click="$router.push('/system/staffs')"> Cancel </a-button>
          </a-form-model-item>
        </a-form-model>
      </a-layout-content>
      <!-- <a-layout-footer> {{ staff }}</a-layout-footer> -->
    </a-layout>
  </div>
</template>

<script>
import store from '@/store'

export default {
  data() {
    return {
      mode: 'new',
      staff: null,
      labelCol: { span: 4 },
      wrapperCol: { span: 20 },
    }
  },
  methods: {
    async update() {
      if (this.staff) {
        const staff = await store.dispatch('staffs/updateStaff', this.staff)
        if (staff) {
          this.staff = staff
          this.$message.success(`Staff ${staff.username} has been updated successfully!`)
        } else {
          this.$message.error('Oops, The staff update failed!')
        }
      }
    },
  },
  async created() {
    if (this.$route.params.id) {
      this.staff = await store.dispatch('staffs/fetchStaff', this.$route.params.id)
    }
  },
}
</script>

<style lang="scss" scoped></style>
